import type { RiveCanvas } from "@rive-app/webgl2-advanced";
import { isPortrait } from "@slot/presentation";
import type { Log, PresentationTicker, ViewportStore } from "@slot/presentation";
import { RiveCanvasLayer } from "../rive/RiveCanvasLayer";

/**
 * The candy factory in its Rive file (the candy-factory-bg project's build): two paintings, one composed for phones (the
 * pipes, lanterns and the fountain's top over the grid, the counter around the controls) and one for the wide screens,
 * each looping its state machine — slow, light motion with nothing to set. Its stills are the same paintings, unmoving:
 * the boot bundle's `background-portrait` and `background-landscape` (`GameDefinition.background`).
 */
export const FACTORY_ART = {
  artboards: { portrait: "Factory Portrait", landscape: "Factory Desktop" },
  stateMachine: "Idle",
} as const;

/** How the background is drawn. */
export interface GameBackgroundTuning {
  /** One drawn frame per this long (engine seconds). */
  frameSeconds: number;
  /** How far over the screen the painting reaches, its still's as much (`GameDefinition.background.overscan`). */
  overscan: number;
}

/**
 * The scene's background, under the transparent game canvas (decision 69's "dom"): the candy factory's painting moving on
 * a canvas of its own drawn straight to the screen (gears turning, a caramel drip, the fountain's shimmer, lanterns,
 * steam), over the shell's still of it. A portrait screen shows the phone's painting and a landscape one the wide one
 * (`isPortrait`, the shell's rule for its still), each covering the screen centred. The still shows until the animation's
 * first frame, and for good when the file fails to load.
 */
export class GameBackground {
  private readonly layer: RiveCanvasLayer;
  private readonly unsubscribe: () => void;

  public constructor(deps: {
    root: HTMLElement;
    url: string;
    viewport: ViewportStore;
    ticker: PresentationTicker;
    runtime: () => Promise<RiveCanvas>;
    tuning: GameBackgroundTuning;
    log: Log;
  }) {
    const { tuning } = deps;
    this.layer = new RiveCanvasLayer({ url: deps.url, stateMachine: FACTORY_ART.stateMachine, overscan: tuning.overscan, frameSeconds: tuning.frameSeconds }, { root: deps.root, runtime: deps.runtime, ticker: deps.ticker, log: deps.log });
    void this.layer.load();
    this.unsubscribe = deps.viewport.subscribe((viewport) => {
      this.layer.show(FACTORY_ART.artboards[isPortrait(viewport.css) ? "portrait" : "landscape"]);
      // The game canvas's resolution rule: the device's pixel ratio, at most 2.
      this.layer.resize(viewport.css.width, viewport.css.height, Math.min(window.devicePixelRatio || 1, 2));
    });
  }

  public dispose(): void {
    this.unsubscribe();
    this.layer.dispose();
  }
}
